import { FailedReason, SdkMessageTypes } from '../constants';
import { IMetaOsHubServices, ITelemetryScenario } from '../interfaces';
import { AppInitState, ContextBridge } from '../hosted-web-content-sdk.interface';
import { HubSdkTelemetryService } from '../telemetry-service';
import { WebContentSdkService } from '../web-content-sdk';
import { BaseMessageHandler } from './base-message-handler';
import { transformContext } from '../utils';

interface IHubConfig {
  readonly apiVersion: number;
  readonly supports: {
    readonly app?: {};
    readonly appEntity?: {};
    readonly appInstallDialog?: {};
    readonly calendar?: {};
    readonly call?: {};
    readonly chat?: {};
    readonly dialog?: {};
    readonly files?: {};
    readonly location?: {};
    readonly log?: {};
    readonly mail?: {};
    readonly media?: {};
    readonly meeting?: {};
    readonly notifications?: {};
    readonly pages?: {
      readonly appButton?: {};
      readonly tabs?: {};
      readonly config?: {};
      readonly backStack?: {};
      readonly fullTrust?: {};
    };
    readonly people?: {};
    readonly teams?: {
      readonly fullTrust?: {};
    };
  };
}

const APP_LOAD_TIMEOUT = 30000; // 30 seconds
const INITIALIZE_TIMEOUT = 15000; // 15 seconds

/**
 * Responsible for handling app initialization related message requests.
 */
export class AppMessageHandler extends BaseMessageHandler {
  private appInitializationScenario?: ITelemetryScenario;
  private registerAppInitMessageHandlers: boolean;
  private appLoadTimerId?: number;

  constructor(
    protected appSdkHost: WebContentSdkService,
    protected hubServices: Partial<IMetaOsHubServices>,
    protected telemetryService?: HubSdkTelemetryService
  ) {
    super(appSdkHost, telemetryService);
    this.appInitializationScenario = this.telemetryService?.createScenario(
      'appInitialization',
      undefined,
      this.appSdkHost.getUserClickTime()
    );
    this.appInitializationScenario?.markPhase('ClickToBeginLoad');
    this.registerAppInitMessageHandlers = appSdkHost.getAppDefinition()?.showTabLoadingIndicator || false;

    this.appLoadTimerId = this.setupAppLoadTimer();
  }

  public initHandlers(): void {
    this.registerHandler(SdkMessageTypes.initialize, this.initialize);
    this.registerHandler(SdkMessageTypes.getContext, this.getContext, {
      errorCallback: this.onGetContextError,
    });

    if (this.registerAppInitMessageHandlers) {
      this.registerHandler(SdkMessageTypes.appInitialization.appLoaded, this.handleAppLoaded);
      this.registerHandler(SdkMessageTypes.appInitialization.success, this.handleSuccess);
      this.registerHandler(SdkMessageTypes.appInitialization.failure, this.handleFailure);
    }
  }

  private setupAppLoadTimer = (): number => {
    if (this.registerAppInitMessageHandlers) {
      return window.setTimeout(() => {
        this.appSdkHost.setAppInitInfo({
          state: AppInitState.Failed,
          reason: FailedReason.Timeout,
        });
        this.failAppInitializationScenario('App Load timed out');
      }, APP_LOAD_TIMEOUT);
    } else {
      return window.setTimeout(() => {
        this.failAppInitializationScenario('SDK initialization timed out');
      }, INITIALIZE_TIMEOUT);
    }
  };

  /**
   * Initialize communication between sdk service and hosted content
   */
  public initialize(_: MessageEvent, appSdkVersion: string): Promise<string[]> {
    this.appInitializationScenario?.markPhase('BeginLoadToInitialized');
    this.telemetryService?.setAppSdkVersion(appSdkVersion);

    if (!this.registerAppInitMessageHandlers) {
      // if the application is not using the appInit messages, we use initialize to stop the app load telemetry scenario
      this.stopAppInitializationScenario();
    }

    this.appSdkHost.setInitialized(true);

    let hubConfig: IHubConfig = {
      apiVersion: 1,
      supports: {
        app: {},
        appInstallDialog: this.hubServices.appInstallDialogService ? {} : undefined,
        appEntity: this.hubServices.appEntityService ? {} : undefined,
        calendar: this.hubServices.calendarService ? {} : undefined,
        call: this.hubServices.callService ? {} : undefined,
        chat: this.hubServices.chatService ? {} : undefined,
        dialog: this.hubServices.dialogService ? {} : undefined,
        files: this.hubServices.filesService ? {} : undefined,
        mail: this.hubServices.mailService ? {} : undefined,
        media: this.hubServices.mediaService ? {} : undefined,
        meeting: this.hubServices.meetingService ? {} : undefined,
        notifications: this.hubServices.notificationService ? {} : undefined,
        location: this.hubServices.locationService ? {} : undefined,
        log: this.hubServices.logService ? {} : undefined,
        pages: this.hubServices.pageService
          ? {
              appButton: this.hubServices.pageService.appButtonService ? {} : undefined,
              tabs: this.hubServices.pageService.tabsService ? {} : undefined,
              config: this.hubServices.pageService.configService ? {} : undefined,
              backStack: this.hubServices.pageService.backStackService ? {} : undefined,
              fullTrust: this.hubServices.pageService.fullTrustService ? {} : undefined,
            }
          : undefined,
        people: this.hubServices.peopleService ? {} : undefined,
        teams: this.hubServices.teamsService
          ? {
              fullTrust: this.hubServices.teamsService?.fullTrustService ? {} : undefined,
            }
          : undefined,
      },
    };

    return Promise.resolve([
      this.appSdkHost.getFrameContext(),
      this.appSdkHost.getHubInfo().clientType,
      '2.0.0',
      JSON.stringify(hubConfig),
    ]);
  }

  /**
   * Retrieve the context of the SDK Provider
   */
  public async getContext(): Promise<ContextBridge> {
    return transformContext(await this.appSdkHost.getAppContext());
  }

  private onGetContextError(error: any, scenario: ITelemetryScenario | undefined): Promise<Partial<ContextBridge>> {
    // Note: getContext can never fail. We might want to change this now that we're moving to promises in
    // the App SDK but there may be back-compat concerns that we should investigate first.
    scenario?.fail(error);
    return Promise.resolve({});
  }

  private handleAppLoaded() {
    this.appSdkHost.setAppInitInfo({ state: AppInitState.Loaded });
  }

  private handleSuccess() {
    this.appSdkHost.setAppInitInfo({ state: AppInitState.Loaded });
    this.stopAppInitializationScenario();
  }

  private handleFailure(_event: MessageEvent, reason: FailedReason) {
    this.appSdkHost.setAppInitInfo({ state: AppInitState.Failed, reason });
    this.failAppInitializationScenario(reason);
  }

  private stopAppInitializationScenario = () => {
    this.clearTimeoutTimer();
    this.appInitializationScenario?.stop();
  };

  private failAppInitializationScenario = (reason?: string) => {
    this.clearTimeoutTimer();
    this.appInitializationScenario?.fail(reason);
  };

  private clearTimeoutTimer = () => {
    window.clearTimeout(this.appLoadTimerId);
  };

  public cleanup() {
    this.clearTimeoutTimer();
  }
}
